/**
 * What a failed camera connect says, to the operator and to the dashboard.
 *
 * Pure, no stores and no I/O, so every rule here is pinned by tests.
 */

/**
 * Shown after "Gagal menyambungkan kamera: " when the browser has no WebUSB.
 * Safari, Firefox and every iPad browser lack it, so a web booth there that is
 * set to a DSLR used to fail with a bare TypeError from `navigator.usb`.
 */
export const NO_WEBUSB_MESSAGE =
  'browser ini tidak bisa memakai kamera DSLR lewat USB. Pakai Chrome atau Edge di komputer, atau aplikasi desktop Dreambooth.'

/** WebUSB exists here (Chromium, Electron). False on Safari, Firefox and iPad. */
export function isWebUsbAvailable(
  nav: unknown = typeof navigator === 'undefined' ? undefined : navigator
): boolean {
  const usb = (nav as { usb?: { getDevices?: unknown } } | undefined)?.usb
  return !!usb && typeof usb.getDevices === 'function'
}

/**
 * The text of whatever a connect threw.
 *
 * Only Error and string used to be read; anything else became "Error tidak
 * diketahui", which is what 346 failed connects in the week to 2026-10-21 told
 * the dashboard. libgphoto2's WASM throws a bare number (a C++ exception
 * pointer), and worker errors can arrive as plain objects.
 */
export function connectErrorText(error: unknown): string {
  if (error instanceof Error) return error.message
  if (typeof error === 'string') return error
  if (typeof error === 'number') return `error internal kamera (kode ${error})`
  if (error && typeof error === 'object') {
    const message = (error as { message?: unknown }).message
    if (typeof message === 'string' && message.trim()) return message
    try {
      const json = JSON.stringify(error)
      if (json && json !== '{}') return json.slice(0, 200)
    } catch {
      // Circular or otherwise unserialisable: fall through.
    }
  }
  return 'Error tidak diketahui'
}

/**
 * A lazily loaded camera-library file (a code chunk or the WASM binary) could
 * not be fetched. After a deploy the booth's running bundle still points at the
 * previous build's hashed files, which the server no longer has; offline looks
 * the same from here, so callers confirm a newer build before acting on it.
 */
export function isStaleAssetError(message: string): boolean {
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|both async and sync fetching of the wasm failed/i.test(
    message
  )
}
